import React, { useState, useEffect } from 'react';
import { useTicketsContext } from '../../contexts/pdfTicketsContext';
import { ChevronLeft, ChevronRight, CheckCircle, XCircle } from 'lucide-react';
import { FIREBASE_STORAGE } from '../../firebase/firebase';
import { ref, listAll, getDownloadURL, uploadBytes, getBlob, deleteObject } from 'firebase/storage';
import { useApiData } from '../../contexts/apiService';
import { RejectedTicketsModal } from './RejectedTicketsModal';
import { useUserAccess, USER_ROLE_LEVEL } from '../../contexts/userAccess';

interface CurrentPdfType {
    name: string,
    downloadURL: string,
    path: string,
}

const Button: React.FC<{ onClick: () => void; children: React.ReactNode; className?: string, disabled?: boolean; }> = ({ onClick, children, className = '', disabled = false }) => (
    <button
        onClick={onClick}
        disabled={disabled}
        className={`px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 ${className}`}
    >
        {children}
    </button>
);

const pdfApprovals: React.FC = () => {
    const { unacceptedPdfs } = useTicketsContext();
    const [currentIndex, setCurrentIndex] = useState<number>(0);
    const [pdfData, setPdfData] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [processingAction, setProcessingAction] = useState<boolean>(false);
    const [actionMessage, setActionMessage] = useState<string | null>(null);
    const [localPdfs, setLocalPdfs] = useState<any[]>([]);
    const { pdfUploaded, pdfScraper } = useApiData();
    const [isScraperLoading, setIsScraperLoading] = useState(false);
    const [processingResult, setProcessingResult] = useState(null);

    const [showRejected, setShowRejected] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [currentPdf, setCurrentPdf] = useState<CurrentPdfType | null>(null)
    const [totalPdfs, setTotalPdfs] = useState(0)
    const { userRole = 'viewer' } = useUserAccess();
    const SCHEDULER_LEVEL = USER_ROLE_LEVEL['scheduler'];
    const showTable = Number(USER_ROLE_LEVEL[userRole]) >= SCHEDULER_LEVEL;

  

    useEffect(() => {
        const safeIndex = Math.min(currentIndex, localPdfs.length - 1);
        setTotalPdfs(localPdfs.length)
        setCurrentPdf(localPdfs.length > 0 ? localPdfs[safeIndex] : null)
    }, [localPdfs, currentIndex])

    // Initialize local PDF list from context
    useEffect(() => {
        if (!showRejected) {
            setLocalPdfs(unacceptedPdfs);
        }
    }, [unacceptedPdfs, showRejected]);


    const handleToggleMode = () => {
        setShowRejected(prev => !prev);
        setCurrentIndex(0);
        setCurrentPdf(null)
    };

    useEffect(() => {
        if (localPdfs.length > 0 && currentIndex >= localPdfs.length) {
            setCurrentIndex(localPdfs.length - 1);
        }
    }, [localPdfs, currentIndex]);

    useEffect(() => {
        if (showRejected) {
            const fetchRejectedPdfs = async () => {
                try {
                    const rejectedRef = ref(FIREBASE_STORAGE, 'rejected_tickets/');
                    const result = await listAll(rejectedRef);
                    const files = await Promise.all(
                        result.items.map(async (item) => {
                            const url = await getDownloadURL(item);
                            const [ticket_number, producer, ...carrierParts] = item.name.replace('.pdf', '').split('_');
                            const carrier = carrierParts.join(' ') || 'Unknown Carrier';
                            return {
                                name: item.name,
                                path: item.fullPath,
                                downloadURL: url,
                                ticket_number,
                                producer: producer || 'Unknown Producer',
                                carrier
                            };
                        })
                    );

                    setLocalPdfs(files);
                } catch (err) {
                    console.error('Error fetching rejected PDFs:', err);
                }
            };
            fetchRejectedPdfs();
        }
    }, [showRejected]);

    // Function to download and display the current PDF
    const fetchPdf = async (url: string) => {
        setLoading(true);
        setError(null);
        setPdfData(null);

        try {
            const response = await fetch(url);
            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            setPdfData(objectUrl);
        } catch (err) {
            console.error('Error fetching PDF:', err);
            setError('Failed to load PDF. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Function to remove the current PDF from local list
    const removeCurrentPdfFromList = () => {
        // Save the current index for later use
        const indexToRemove = currentIndex;

        // Update local PDFs state
        setLocalPdfs(prevPdfs => {
            const newPdfs = [...prevPdfs];
            newPdfs.splice(indexToRemove, 1);
            return newPdfs;
        });
    };

    // Move file to unassigned_tickets folder
    const acceptPdf = async (fileRef: string, fileName: string) => {
        setProcessingAction(true);
        setActionMessage("Moving file to unassigned tickets...");

        try {
            const sourceRef = ref(FIREBASE_STORAGE, fileRef);
            const fileBlob = await getBlob(sourceRef);
            const destRef = ref(FIREBASE_STORAGE, `unassigned_tickets/${fileName}`);
            await uploadBytes(destRef, fileBlob);
            // Delete the original file
            await deleteObject(sourceRef);

            // Remove from local list and update message
            setActionMessage("Ticket accepted");
            removeCurrentPdfFromList();
            setProcessingAction(false);
            pdfUploaded().catch(err => console.error('Error in background API update:', err));

            return true;
        } catch (err) {
            console.error('Error moving file:', err);
            setActionMessage("Error moving file. Please try again.");
            setProcessingAction(false);
            return false;
        }
    };

    const rejectPdf = async (fileRef: string, fileName: string) => {
        setProcessingAction(true);
        setActionMessage("Moving file to rejected tickets...");

        try {
            // Get a reference to the original file
            const sourceRef = ref(FIREBASE_STORAGE, fileRef);
            // Download the file as a blob
            const fileBlob = await getBlob(sourceRef);
            // Create a reference to the destination location
            const destRef = ref(FIREBASE_STORAGE, `rejected_tickets/${fileName}`);
            // Upload to the new location
            await uploadBytes(destRef, fileBlob);
            // Delete the original file
            await deleteObject(sourceRef);

            // Remove from local list and update message
            removeCurrentPdfFromList();
            setActionMessage("Ticket rejected");
            setProcessingAction(false);

            return true;
        } catch (err) {
            console.error('Error moving file:', err);
            setActionMessage("Error moving file. Please try again.");
            setProcessingAction(false);
            return false;
        }
    };

    // Navigate to the next PDF
    const goToNext = () => {
        if (currentIndex < totalPdfs - 1) {
            setCurrentIndex(currentIndex + 1);
        }
    };

    // Navigate to the previous PDF
    const goToPrevious = () => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
        }
    };

    const handleAccept = async () => {
        if (!currentPdf || processingAction) return;
        await acceptPdf(currentPdf.path, currentPdf.name);
    };

    const handleReject = async () => {
        if (!currentPdf || processingAction) return;
        await rejectPdf(currentPdf.path, currentPdf.name);
    };

    const handleDownload = () => {
        if (!pdfData) return;
        const link = document.createElement('a');
        link.href = pdfData;
        link.download = currentPdf?.name || 'ticket.pdf';
        link.click();
    };

    useEffect(() => {
        if (currentPdf && localPdfs.length > 0) {
            fetchPdf(currentPdf.downloadURL);
        } else if (localPdfs.length === 0) {
            // Clear PDF data if there are no PDFs left
            setPdfData(null);
        }
    }, [currentIndex, localPdfs, currentPdf]);

    // Clean up object URLs when component unmounts or when PDF changes
    useEffect(() => {
        return () => {
            if (pdfData) {
                URL.revokeObjectURL(pdfData);
            }
        };
    }, [pdfData]);

    const handlePdfScraperTrigger = async () => {
        setProcessingResult(null);
        setIsScraperLoading(true);

        try {
            const processedCount = await pdfScraper();
            if (processedCount !== undefined) {
                console.log(`Handler received: ${processedCount} documents processed`);
                setProcessingResult(processedCount);
            }
        } catch (error) {
            console.error('Error in trigger:', error);
        } finally {
            // Always set loading back to false when done, whether successful or not
            setIsScraperLoading(false);
        }
    }

    // if (unacceptedPdfs.length === 0) {
    //     return <div className="p-4">No unaccepted PDFs available.</div>;
    // }

    if (!showTable) {
        return <p>Not Allowed</p>;
    }
    return (
        <div className="flex flex-col h-screen bg-gray-100" >
            {/* Header with navigation controls */}
            <div className="bg-white p-4 shadow-md flex items-center justify-between" >
                <h1 className="text-xl font-bold" > {showRejected ? 'Rejected Tickets' : 'PDFs Pending Approval'} </h1>
                < div className="flex gap-3" >
                    {!showRejected && (
                        <Button
                            onClick={handlePdfScraperTrigger}
                            disabled={isScraperLoading}
                        >
                            {isScraperLoading ? 'Processing...' : 'Get PDFs'}
                        </Button>
                    )}
                    <Button onClick={handleToggleMode}>
                        {showRejected ? 'Pending Approval Tickets' : 'Rejected Tickets'}
                    </Button>
                </div>
                < div className="flex items-center space-x-4" >
                    <button onClick={goToPrevious} disabled={currentIndex === 0} className="text-blue-600 p-2 hover:bg-blue-100 rounded-full" >
                        <ChevronLeft size={24} />
                    </button>
                    <span> {currentIndex + 1} of {totalPdfs} </span>
                    <button onClick={goToNext} disabled={currentIndex === totalPdfs - 1} className="text-blue-600 p-2 hover:bg-blue-100 rounded-full" >
                        <ChevronRight size={24} />
                    </button>
                </div>
            </div>

            <div className="bg-gray-200 p-3 flex items-center justify-between" >
                <h2 className="font-medium" > {currentPdf?.name} </h2>
                {
                    !showRejected ? (
                        <div className="flex space-x-3" >
                            <button onClick={handleAccept} className="bg-green-500 text-white px-4 py-2 rounded flex items-center" > <CheckCircle className="mr-2" size={18} /> Accept Ticket </button>
                            < button onClick={handleReject} className="bg-red-500 text-white px-4 py-2 rounded flex items-center" > <XCircle className="mr-2" size={18} /> Reject Ticket </button>
                        </div>
                    ) : (
                        <div className="flex space-x-3" >
                            <button onClick={() => setModalOpen(true)} className="bg-blue-500 text-white px-4 py-2 rounded" > Rejected Tickets List </button>
                            < button onClick={handleDownload} className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded transition" > Download PDF </button>

                        </div>
                    )
                }
            </div>
            {/* PDF file name with accept/reject buttons */}
            < div className="flex-grow p-4 flex flex-col items-center overflow-auto" >
                {
                    loading ? (
                        <div className="flex justify-center items-center h-full" >
                            <div className="animate-spin h-12 w-12 border-4 border-blue-500 border-t-transparent rounded-full"> </div>
                        </div>
                    ) : pdfData ? (
                        <iframe
                            src={`${pdfData}#&navpanes=0&scrollbar=0`}
                            className="w-full h-full border border-gray-300 rounded"
                            title={`PDF Viewer - ${currentPdf?.name}`}
                        />
                    ) : (
                        <p>No ticket selected.</p>
                    )}
            </div>

            {
                modalOpen && (
                    <RejectedTicketsModal
                        show={modalOpen}
                        onHide={() => setModalOpen(false)
                        }
                        data={localPdfs}
                        onSelectTicket={(pdfUrl, index) => {
                            setPdfData(pdfUrl);
                            setCurrentIndex(index);
                            setModalOpen(false);
                        }}
                    />
                )}

        </div>
    );
};

export default pdfApprovals;
